import { useEffect, useState } from "react";
import { db, auth, storage } from "@/lib/firebase";
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, onSnapshot, orderBy, Timestamp, setDoc, limit } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

type PendingUser = {
  id: string;
  email: string;
  fullName: string;
  company_name: string | null;
  role: string;
  created_by: string;
  status: string;
  createdAt: string;
};

export const PendingUserApprovals = () => {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchPendingUsers();
    subscribeToPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    const pendingQuery = query(
      collection(db, "pending_user_accounts"),
      where("status", "==", "pending"),
      orderBy("createdAt", "desc")
    );
    
    const snapshot = await getDocs(pendingQuery);
    const data = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as PendingUser));

    setPendingUsers(data);
    setLoading(false);
  };

  const subscribeToPendingUsers = () => {
    const pendingQuery = query(
      collection(db, "pending_user_accounts"),
      where("status", "==", "pending")
    );

    const unsubscribe = onSnapshot(pendingQuery, () => {
      fetchPendingUsers();
    });

    return () => {
      unsubscribe();
    };
  };

  const handleApprove = async (pendingUser: PendingUser) => {
    setProcessingId(pendingUser.id);
    try {
      // Call cloud function to create team user
      const createUserFunction = httpsCallable(getFunctions(), "createTeamUser");
      await createUserFunction({
        email: pendingUser.email,
        fullName: pendingUser.fullName,
        companyName: pendingUser.company_name,
        role: pendingUser.role,
        pendingUserId: pendingUser.id,
      });

      // Mark as approved
      await updateDoc(doc(db, "pending_user_accounts", pendingUser.id), {
        status: "approved",
        reviewed_at: Timestamp.now(),
      });

      // Create notification for the manager who requested
      await addDoc(collection(db, "notifications"), {
        userId: pendingUser.created_by,
        type: "worker_approval",
        title: "User Account Approved",
        message: `${pendingUser.fullName} (${pendingUser.email}) has been approved and can now access the system.`,
        createdAt: Timestamp.now(),
      });

      toast({
        title: "User Approved",
        description: `${pendingUser.fullName} has been created successfully`,
      });

      fetchPendingUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (pendingUser: PendingUser) => {
    setProcessingId(pendingUser.id);
    try {
      await updateDoc(doc(db, "pending_user_accounts", pendingUser.id), {
        status: "rejected",
        reviewed_at: Timestamp.now(),
      });

      // Create notification for the manager who requested
      await addDoc(collection(db, "notifications"), {
        userId: pendingUser.created_by,
        type: "worker_approval",
        title: "User Account Rejected",
        message: `The request to create account for ${pendingUser.fullName} (${pendingUser.email}) was rejected.`,
        createdAt: Timestamp.now(),
      });

      toast({
        title: "Request Rejected",
        description: `Rejected account creation for ${pendingUser.fullName}`,
      });

      fetchPendingUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Don't show anything while loading to avoid flash of loading state
  if (loading) {
    return null;
  }

  if (pendingUsers.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending User Approvals ({pendingUsers.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {pendingUsers.map((user) => (
          <div
            key={user.id}
            className="flex items-center justify-between p-4 border rounded-lg"
          >
            <div className="flex-1">
              <p className="font-medium">{user.fullName}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Role: {user.role} • Requested:{" "}
                {new Date(user.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handleApprove(user)}
                disabled={processingId === user.id}
              >
                {processingId === user.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleReject(user)}
                disabled={processingId === user.id}
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
